import React from 'react';
import { Link } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import 'screens/Public/DynamicArticle/DynamicArticle.css';

const containsHTML = (text: string) => /<[a-z][\s\S]*?>/i.test(text);

// Convert literal bullet characters (•) to proper Markdown list syntax
const convertBulletsToMarkdown = (content: string): string => {
  // Add newlines before standalone bullets that follow text
  let processed = content.replace(/([^\n])\s*•\s+/g, '$1\n- ');
  // Handle bullets at the very start of the string
  processed = processed.replace(/^•\s+/, '- ');
  return processed;
};

interface Props {
  content: string;
}

/**
 * The body of a system message: the markdown card with the 4px violet left
 * border (styles under .message-bubble.system-message in ThreadPane.css).
 * Moved unchanged from ConversationThreadPage (Asana 1219952262403623,
 * Decision 3: reuse the existing renderer). HTML bodies go through DOMPurify
 * into .article-content; everything else is markdown, where a relative link
 * becomes a router Link so notices can deep-link inside the app.
 */
export const SystemMessageCard: React.FC<Props> = ({ content }) => {
  if (containsHTML(content)) {
    return (
      <div
        className="article-content"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'img', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'span', 'div'],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel', 'class', 'style', 'data-type', 'text'],
          }),
        }}
      />
    );
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children }) => {
          const isExternal = href?.startsWith('http');
          const isMailto = href?.startsWith('mailto:');

          if (isMailto) {
            return <a href={href}>{children}</a>;
          }

          if (isExternal) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            );
          }

          return <Link to={href || '#'}>{children}</Link>;
        },
      }}
    >
      {convertBulletsToMarkdown(content)}
    </ReactMarkdown>
  );
};

export default SystemMessageCard;
